import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { useDebouncedValue } from "@/hooks/useDebouncedValue"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ChevronsUpDown, Loader2, Send } from "lucide-react"
import { useMemo, useState } from "react"
import {
  inquiryStatusLabel,
  useInquiryList,
  type SupplierInquiryListItem,
} from "../../../api/supplierInquiriesApi"
import { formatDate } from "../../../utils/date"

interface InquiryPickerProps {
  /** Reports the whole row so the caller can show what was picked before committing. */
  onSelect: (inquiry: SupplierInquiryListItem) => void
  /** What is currently staged, shown on the trigger. */
  selectedLabel?: string
  disabled?: boolean
  className?: string
}

/**
 * Searchable open-inquiry picker for connecting an unmatched email.
 *
 * A native <select> can't carry enough to decide on — a reference alone says nothing
 * about who it went to — and it degrades badly once an org has more open inquiries than
 * fit on screen. This is the module's established picker shape (SupplierPicker /
 * CustomerPicker): server-side search, supplier name as the primary line, reference and
 * dates beneath it.
 *
 * Picking only STAGES an inquiry; the caller connects on an explicit click. Connecting
 * straight from a dropdown selection reads as browsing, not committing, and this action
 * cannot be undone from here.
 */
export function InquiryPicker({
  onSelect,
  selectedLabel,
  disabled,
  className,
}: InquiryPickerProps) {
  const { translate } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const debouncedQuery = useDebouncedValue(query, 300)

  const { data, isFetching } = useInquiryList({
    bucket: "open",
    q: debouncedQuery || undefined,
    sort: "created_desc",
    limit: 50,
    enabled: open,
  })

  const inquiries = useMemo(
    () => data?.pages.flatMap((page) => page.items) ?? [],
    [data],
  )

  function handleOpenChange(next: boolean) {
    setOpen(next)
    if (!next) setQuery("")
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "flex w-full items-center justify-between gap-2 rounded-md border border-grey4 bg-grey2 px-3 py-2 text-sm text-white transition-colors",
            "hover:border-grey5 focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring",
            "disabled:pointer-events-none disabled:opacity-50",
            className,
          )}
        >
          <span
            className={cn(
              "flex min-w-0 items-center gap-2 truncate",
              selectedLabel ? "text-white" : "text-grey5",
            )}
          >
            <Send size={13} className="shrink-0" />
            {selectedLabel ?? translate("MC_INQ_MATCH_SEARCH_OPEN_INQUIRIES")}
          </span>
          <ChevronsUpDown size={12} className="shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-(--radix-popover-trigger-width) p-0"
      >
        <Command shouldFilter={false}>
          <CommandInput
            value={query}
            onValueChange={setQuery}
            placeholder={translate("MC_INQ_MATCH_SEARCH_OPEN_INQUIRIES")}
          />
          <CommandList>
            {isFetching && inquiries.length === 0 ? (
              <div className="flex items-center gap-2 p-3 text-xs text-grey5">
                <Loader2 size={12} className="animate-spin" />
                {translate("MC_INQ_MATCH_LOADING")}
              </div>
            ) : (
              <CommandEmpty>
                {translate("MC_INQ_MATCH_NO_OPEN_INQUIRIES")}
              </CommandEmpty>
            )}
            <CommandGroup>
              {inquiries.map((inquiry) => (
                <CommandItem
                  key={inquiry.id}
                  value={inquiry.id}
                  onSelect={() => {
                    onSelect(inquiry)
                    handleOpenChange(false)
                  }}
                  className="flex-col items-start gap-0.5"
                >
                  <span className="truncate text-sm text-white">
                    {inquiry.supplierName ?? "—"}
                  </span>
                  <span className="truncate text-xs text-grey5">
                    {[
                      inquiry.reference,
                      inquiryStatusLabel(inquiry.status, translate),
                      inquiry.dueAt
                        ? translate("MC_INQ_MATCH_DUE", {
                            date: formatDate(inquiry.dueAt),
                          })
                        : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
